/**
 * GFG-364 — the row icon control (resend, preview, remove, copy link) both contact tables use: the
 * campaign-contacts table (`ContactRowActions.tsx`) and the admin Manage Contacts table
 * (`admin/campaigns/ContactActionsCell.tsx`). Each cell keeps its own rules for WHEN a control is
 * refused and what its tooltip says; this file only renders the three states.
 *
 * A REFUSED CONTROL IS `aria-disabled`, NOT `disabled`. Every refusal comes with a sentence
 * explaining it — the campaign lock, the contact's status, "already resent, try again in 6h" — and
 * a truly `disabled` button drops out of the tab order, loses its pointer events and is skipped by
 * screen-reader control navigation, so that sentence could only ever be discovered with a mouse,
 * hidden from the people least able to infer it from a greyed-out icon. So the button stays
 * focusable (MUI opens the tooltip on focus as well as hover, which only reaches the keyboard
 * because the button keeps its tab stop), points `aria-describedby` at the reason, and looks
 * exactly as disabled as the prototype's. Whether its click still runs is the caller's choice: the
 * campaign-contacts table answers a refused click with its refusal toast; the admin table passes
 * `inertWhenRefused`, and a refused click does nothing.
 *
 * `disabled` IS THE OPPOSITE CASE and takes precedence: the action is not refused, it does not
 * exist yet (see `actionsDisabled` on CampaignContactsModal). There is nothing to say on click, so
 * the button is genuinely `disabled` — which is also what a screen reader should hear, since
 * "dimmed, unavailable" is the truth and a focusable control with nothing to explain is not. MUI
 * drops pointer events on a disabled button, so the tooltip needs a wrapper element to hover; the
 * `<span>` exists for that and for nothing else.
 */
import { IconButton, Tooltip } from "@mui/material";
import type { ReactNode } from "react";

/** Prototype `.cmp-resend-btn` / `.cmp-preview-btn` / `.cmp-contact-remove`. */
const actionButtonSx = {
  p: "4px",
  mr: "2px",
  color: "var(--color-rodeo-dust)",
  borderRadius: "6px",
  "&:hover": { color: "var(--color-dark-dust)", bgcolor: "var(--color-wax)" },
} as const;

/**
 * Prototype `.cmp-resend-btn:disabled` — the same muted grey, driven off the props instead of
 * `.Mui-disabled` (a refused control never gets that class), with `cursor: default` replacing the
 * pointer without taking the button out of the tab order.
 */
const refusedActionSx = {
  ...actionButtonSx,
  color: "var(--color-smoke)",
  cursor: "default",
  "&:hover": { color: "var(--color-smoke)", bgcolor: "transparent" },
} as const;

interface ContactActionButtonProps {
  /** Muted and `aria-disabled`, but still focusable and still announced — see the file header. */
  refused: boolean;
  /** The action does not exist yet: a real `disabled` button. Wins over `refused`. */
  disabled?: boolean;
  /** A refused click does nothing instead of calling `onClick` (the admin table). */
  inertWhenRefused?: boolean;
  /** Tooltip text: the action when live, the reason it is refused when not. */
  title: string;
  label: string;
  /** Element saying WHY this control is refused; only referenced while it is. */
  describedById?: string;
  onClick: () => void;
  children: ReactNode;
}

export default function ContactActionButton({
  refused,
  disabled = false,
  inertWhenRefused = false,
  title,
  label,
  describedById,
  onClick,
  children,
}: ContactActionButtonProps) {
  const blocked = disabled || refused;
  const refusedFocusable = refused && !disabled;

  function handleClick() {
    if (refused && inertWhenRefused) return;
    onClick();
  }

  const button = (
    <IconButton
      size="small"
      disabled={disabled}
      aria-disabled={blocked || undefined}
      aria-label={label}
      aria-describedby={refusedFocusable ? describedById : undefined}
      disableRipple={refusedFocusable}
      onClick={disabled ? undefined : handleClick}
      sx={blocked ? refusedActionSx : actionButtonSx}
    >
      {children}
    </IconButton>
  );

  return <Tooltip title={title}>{disabled ? <span>{button}</span> : button}</Tooltip>;
}
